// ts-rest contract of the notes on the corporate-actions calendar (SEP-349
// part 3).
//
//   GET  /api/coac/calendar-notes             the notes that the caller may read
//   POST /api/coac/calendar-notes             Ops adds a note
//   PUT  /api/coac/calendar-notes/:id         Ops changes a note
//   POST /api/coac/calendar-notes/:id/remove  Ops removes a note
//
// A caller who is not in Operations gets the notes marked `all` only. The
// server applies the rule: the response never holds an Ops-only note.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

export const coacCalendarNoteVisibilitySchema = z.enum(['all', 'ops']);
export type CoacCalendarNoteVisibility = z.infer<typeof coacCalendarNoteVisibilitySchema>;

export const coacCalendarNoteSchema = z.object({
    id: z.string(),
    // One anchor: a corporate action, or a day (YYYY-MM-DD).
    coacActionId: z.string().nullable(),
    noteDate: z.string().nullable(),
    body: z.string(),
    visibility: coacCalendarNoteVisibilitySchema,
    createdAt: z.string(),
    createdByName: z.string().nullable(),
    editedAt: z.string().nullable(),
});
export type CoacCalendarNote = z.infer<typeof coacCalendarNoteSchema>;

const noteBody = z.string().trim().min(1).max(2000);

export const coacAddCalendarNoteSchema = z.object({
    coacActionId: z.string().nullable(),
    noteDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    body: noteBody,
    visibility: coacCalendarNoteVisibilitySchema,
});
export type CoacAddCalendarNote = z.infer<typeof coacAddCalendarNoteSchema>;

export const coacUpdateCalendarNoteSchema = z.object({
    body: noteBody,
    visibility: coacCalendarNoteVisibilitySchema,
});
export type CoacUpdateCalendarNote = z.infer<typeof coacUpdateCalendarNoteSchema>;

export const coacCalendarContract = c.router(
    {
        listNotes: {
            method: 'GET',
            path: '/coac/calendar-notes',
            responses: {
                200: z.object({ notes: z.array(coacCalendarNoteSchema) }),
            },
            summary:
                'The calendar notes that the caller may read: every note for Ops, the notes for all users for each other persona',
        },
        addNote: {
            method: 'POST',
            path: '/coac/calendar-notes',
            body: coacAddCalendarNoteSchema,
            responses: {
                200: coacCalendarNoteSchema,
                400: errorBody,
            },
            summary: 'Ops adds a note to a corporate action or to a day of the calendar',
        },
        updateNote: {
            method: 'PUT',
            path: '/coac/calendar-notes/:id',
            pathParams: z.object({ id: z.string() }),
            body: coacUpdateCalendarNoteSchema,
            responses: {
                200: coacCalendarNoteSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Ops changes the words or the visibility of a calendar note',
        },
        removeNote: {
            method: 'POST',
            path: '/coac/calendar-notes/:id/remove',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                404: errorBody,
            },
            summary: 'Ops removes a calendar note',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
